import React, { useState } from "react";
import ApiServices from "../Services/ApiServices";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faPaperPlane,
  faUser,
  faMessage,
  faHeadset,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import extractErrorMessage from "../../Helpers/APIHelper";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Header from "../Shared/Header";
import { toastHelper } from "../../Helpers/toastHelper";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ContactUs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Yup validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("الاسم مطلوب")
      .min(2, "الاسم يجب أن يكون على الأقل حرفين")
      .max(50, "الاسم يجب أن لا يتجاوز 50 حرف")
      .matches(/^[\u0600-\u06FF\s]+$/, "الاسم يجب أن يحتوي على أحرف عربية فقط"),
    email: Yup.string()
      .required("البريد الإلكتروني مطلوب")
      .email("البريد الإلكتروني غير صحيح")
      .max(100, "البريد الإلكتروني يجب أن لا يتجاوز 100 حرف"),
    message: Yup.string()
      .required("الرسالة مطلوبة")
      .min(10, "الرسالة يجب أن تكون على الأقل 10 أحرف")
      .max(1000, "الرسالة يجب أن لا تتجاوز 1000 حرف"),
  });

  const initialValues = {
    name: "",
    email: "",
    message: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      await ApiServices.post("ContactUs/Create", {
        name: values.name,
        email: values.email,
        message: values.message,
      });
      resetForm();
      toastHelper.success("تم إرسال رسالتك بنجاح! سنرد عليك قريبًا.");
      navigate("/");
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(
          error?.msg || "حدث خطأ أثناء إرسال الرسالة. حاول مرة أخرى."
        );
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: faEnvelope,
      title: "البريد الإلكتروني",
      value: "emthanyplatform@gmail.com",
      desc: "نرد خلال 24 ساعة",
      accent: "#2563eb",
      soft: "#eff6ff",
    },
    {
      icon: faPhone,
      title: "رقم الهاتف",
      value: "+201554147681",
      desc: "واتساب",
      accent: "#059669",
      soft: "#f0fdf4",
    },
    {
      icon: faMapMarkerAlt,
      title: "العنوان",
      value: "القاهرة، مصر",
      desc: "المقر الرئيسي",
      accent: "#9333ea",
      soft: "#faf5ff",
    },
  ];

  return (
    <>
      <Header />

      <section className="ct-root" id="contact">
        <div className="ct-container">

          {/* ── header ── */}
          <motion.div
            className="ct-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="ct-eyebrow" variants={fadeUp} custom={0}>
              <span className="ct-eyebrow-dot" />
              تواصل معنا
            </motion.div>
            <motion.h2 className="ct-title" variants={fadeUp} custom={1}>
              نحن هنا <em>للاستماع إليك</em>
            </motion.h2>
            <motion.p className="ct-subtitle" variants={fadeUp} custom={2}>
              لديك استفسار أو اقتراح؟ فريق الدعم جاهز لمساعدتك في أي وقت. لا تتردد في مراسلتنا.
            </motion.p>
          </motion.div>

          {/* ── content grid ── */}
          <div className="ct-content">

            {/* ── info column ── */}
            <motion.div
              className="ct-info"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >
              {contactInfo.map((info, i) => (
                <motion.div
                  key={i}
                  className="ct-info-card"
                  variants={fadeUp}
                  custom={i}
                  whileHover="hover"
                  initial="rest"
                  animate="rest"
                >
                  <div
                    className="ct-info-icon"
                    style={{ background: info.soft, color: info.accent }}
                  >
                    <FontAwesomeIcon icon={info.icon} />
                  </div>
                  <div className="ct-info-text">
                    <span className="ct-info-label">{info.title}</span>
                    <p className="ct-info-value">{info.value}</p>
                    <span className="ct-info-desc">{info.desc}</span>
                  </div>
                  <motion.div
                    className="ct-info-bar"
                    style={{ background: info.accent }}
                    variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.div>
              ))}

              {/* support card */}
              <motion.div
                className="ct-support"
                variants={fadeUp}
                custom={3}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div className="ct-support-icon">
                  <FontAwesomeIcon icon={faHeadset} />
                </div>
                <div>
                  <h4 className="ct-support-title">دعم فني متميز</h4>
                  <p className="ct-support-desc">
                    فريقنا متاح لمساعدتك في حل أي مشكلة تقنية قد تواجهها.
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* ── form column ── */}
            <motion.div
              className="ct-form-wrap"
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="ct-form-header">
                <h3>أرسل رسالة</h3>
                <p>سنقوم بالرد عليك في أقرب وقت ممكن</p>
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, errors, touched }) => (
                  <Form className="ct-form">
                    <div className="ct-field">
                      <label>الاسم الكامل</label>
                      <div className="ct-input-box">
                        <Field
                          name="name"
                          type="text"
                          placeholder="مثال: أحمد محمد"
                          className={`ct-input ${errors.name && touched.name ? "ct-input-err" : ""}`}
                        />
                        <FontAwesomeIcon icon={faUser} className="ct-field-icon" />
                      </div>
                      <ErrorMessage name="name" component="div" className="ct-err" />
                    </div>

                    <div className="ct-field">
                      <label>البريد الإلكتروني</label>
                      <div className="ct-input-box">
                        <Field
                          name="email"
                          type="email"
                          placeholder="example@email.com"
                          className={`ct-input ${errors.email && touched.email ? "ct-input-err" : ""}`}
                        />
                        <FontAwesomeIcon icon={faEnvelope} className="ct-field-icon" />
                      </div>
                      <ErrorMessage name="email" component="div" className="ct-err" />
                    </div>

                    <div className="ct-field">
                      <label>الرسالة</label>
                      <div className="ct-input-box">
                        <Field
                          name="message"
                          as="textarea"
                          rows="5"
                          placeholder="اكتب استفسارك هنا..."
                          className={`ct-input ct-textarea ${errors.message && touched.message ? "ct-input-err" : ""}`}
                        />
                        <FontAwesomeIcon icon={faMessage} className="ct-field-icon ct-field-icon-ta" />
                      </div>
                      <ErrorMessage name="message" component="div" className="ct-err" />
                    </div>

                    <motion.button
                      type="submit"
                      className="ct-submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={isSubmitting || loading}
                    >
                      {isSubmitting || loading ? "جاري الإرسال..." : (
                        <>
                          <span>إرسال الرسالة</span>
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </>
                      )}
                    </motion.button>
                  </Form>
                )}
              </Formik>
            </motion.div>

          </div>
        </div>
      </section>

      <style>{`
        /* ── root ── */
        .ct-root {
            font-family: "Zain", sans-serif;
          direction: rtl;
          position: relative;
          padding: 120px 0 100px;
          background: #fff;
          overflow: hidden;
          isolation: isolate;
        }

        /* ── container ── */
        .ct-container {
          position: relative; z-index: 2;
          max-width: 1260px; margin: 0 auto; padding: 0 40px;
        }
        @media(min-width:1200px){ .ct-container { padding: 0 60px; } }

        /* ── header ── */
        .ct-header { text-align: center; margin-bottom: 72px; }

        .ct-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .ct-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: ct-pulse 2.2s ease-in-out infinite;
        }
        @keyframes ct-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .ct-title {
          font-size: clamp(2.2rem, 4vw, 3.6rem);
          font-weight: 900; color: #0f172a;
          margin: 0 0 18px; line-height: 1.2;
          letter-spacing: -0.025em;
        }
        .ct-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ct-subtitle {
          font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0;
        }

        /* ── content grid ── */
        .ct-content {
          display: grid;
          grid-template-columns: 1fr 1.4fr;
          gap: 28px;
          align-items: start;
        }
        @media(max-width:960px){
          .ct-content {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          .ct-form-wrap { order: -1; }
        }

        /* ── info cards ── */
        .ct-info {
          display: flex; flex-direction: column; gap: 16px;
        }

        .ct-info-card {
          position: relative;
          border-radius: 20px;
          border: 1px solid rgba(15,23,42,0.08);
          background: #fff;
          overflow: hidden;
          display: flex; align-items: center; gap: 18px;
          padding: 22px 20px;
          transition: border-color 0.3s, box-shadow 0.3s;
        }
        .ct-info-card:hover {
          border-color: rgba(15,23,42,0.15);
          box-shadow: 0 12px 32px rgba(15,23,42,0.08);
        }

        .ct-info-icon {
          width: 54px; height: 54px; border-radius: 16px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.3rem; flex-shrink: 0;
        }

        .ct-info-text {
          display: flex; flex-direction: column; gap: 2px;
        }
        .ct-info-label {
          font-size: 0.82rem; color: #94a3b8; font-weight: 700;
        }
        .ct-info-value {
          font-size: 1.05rem; font-weight: 800; color: #0f172a;
          margin: 0; direction: ltr; text-align: right;
        }
        .ct-info-desc {
          font-size: 0.78rem; color: #94a3b8; font-weight: 600;
        }

        .ct-info-bar {
          position: absolute; bottom: 0; left: 0; right: 0;
          height: 3px;
        }

        /* ── support card ── */
        .ct-support {
          display: flex; align-items: flex-start; gap: 18px;
          padding: 28px 24px;
          border-radius: 20px;
          background: #0f172a;
          color: #fff;
          margin-top: 8px;
        }
        .ct-support-icon {
          font-size: 1.8rem; color: #6366f1;
          flex-shrink: 0; margin-top: 2px;
        }
        .ct-support-title {
          font-size: 1.15rem; font-weight: 900;
          color: #fff;
          margin: 0 0 8px;
        }
        .ct-support-desc {
          font-size: 0.92rem; color: #94a3b8;
          line-height: 1.65; margin: 0;
        }

        /* ── form wrap ── */
        .ct-form-wrap {
          border-radius: 24px;
          border: 1px solid rgba(15,23,42,0.08);
          background: #fff;
          padding: 36px 32px;
          box-shadow: 0 8px 32px rgba(15,23,42,0.06);
        }
        .ct-form-header {
          text-align: center; margin-bottom: 30px;
        }
        .ct-form-header h3 {
          font-size: 1.6rem; font-weight: 900;
          color: #0f172a; margin: 0 0 8px;
        }
        .ct-form-header p {
          font-size: 0.95rem; color: #64748b; margin: 0;
        }

        .ct-form {
          display: flex; flex-direction: column; gap: 22px;
        }

        .ct-field label {
          display: block; margin-bottom: 8px;
          color: #334155; font-weight: 700; font-size: 0.92rem;
        }
        .ct-input-box {
          position: relative;
        }
        .ct-input {
          width: 100%;
          padding: 13px 18px 13px 46px;
          border-radius: 14px;
          border: 1.5px solid rgba(15,23,42,0.1);
          background: #f8fafc;
          font-size: 0.98rem; color: #0f172a;
          font-family: inherit;
          transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
        }
        .ct-input:focus {
          outline: none;
          border-color: #6366f1;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(232,146,10,0.08);
        }
        .ct-input-err {
          border-color: #ef4444 !important;
          background: #fef2f2;
        }
        .ct-textarea {
          min-height: 120px; resize: vertical;
        }
        .ct-field-icon {
          position: absolute; left: 16px; top: 50%;
          transform: translateY(-50%);
          color: #94a3b8; font-size: 1rem;
          transition: color 0.25s;
          pointer-events: none;
        }
        .ct-field-icon-ta {
          top: 18px; transform: none;
        }
        .ct-input:focus ~ .ct-field-icon {
          color: #6366f1;
        }
        .ct-err {
          color: #ef4444; font-size: 0.82rem;
          font-weight: 600; margin-top: 6px;
        }

        /* submit */
        .ct-submit {
          width: 100%; padding: 15px;
          background: #6366f1;
          color: #fff; border: none;
          border-radius: 14px;
          font-family: inherit; font-size: 1.05rem; font-weight: 800;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          box-shadow: 0 10px 28px -6px #6366f1;
          transition: box-shadow 0.3s;
        }
        .ct-submit:hover {
          box-shadow: 0 16px 36px -6px #6366f1;
        }
        .ct-submit:disabled {
          opacity: 0.7; cursor: not-allowed;
        }

        @media(max-width:768px){
          .ct-root { padding: 80px 0 60px; }
          .ct-container { padding: 0 20px; }
          .ct-header { margin-bottom: 40px; }
          .ct-title { margin-bottom: 12px; }
          .ct-subtitle { font-size: 0.98rem; }
          .ct-form-wrap { padding: 30px 24px; }
        }

        @media(max-width:480px){
          .ct-root { padding: 60px 0 48px; }
          .ct-container { padding: 0 16px; }
          .ct-header { margin-bottom: 32px; }
          .ct-eyebrow { margin-bottom: 14px; font-size: 0.78rem; }
          .ct-title { font-size: 1.8rem; margin-bottom: 10px; }
          .ct-subtitle { font-size: 0.9rem; line-height: 1.7; }
          
          .ct-form-wrap { padding: 24px 16px; border-radius: 20px; }
          .ct-form-header { margin-bottom: 24px; }
          .ct-form-header h3 { font-size: 1.35rem; }
          .ct-form-header p { font-size: 0.85rem; }
          .ct-form { gap: 16px; }
          .ct-field label { font-size: 0.85rem; margin-bottom: 6px; }
          .ct-input { padding: 12px 14px 12px 42px; font-size: 16px; border-radius: 12px; }
          .ct-textarea { min-height: 100px; }
          .ct-field-icon { left: 14px; font-size: 0.95rem; }
          .ct-field-icon-ta { top: 15px; }
          .ct-err { font-size: 0.78rem; margin-top: 4px; }
          .ct-submit { padding: 13px; font-size: 0.98rem; border-radius: 12px; }

          .ct-info { gap: 12px; }
          .ct-info-card { padding: 16px 14px; gap: 12px; border-radius: 16px; }
          .ct-info-icon { width: 44px; height: 44px; border-radius: 12px; font-size: 1.1rem; }
          .ct-info-label { font-size: 0.78rem; }
          .ct-info-value { font-size: 0.95rem; word-break: break-all; }
          .ct-info-desc { font-size: 0.75rem; }
          
          .ct-support { padding: 20px 16px; gap: 12px; border-radius: 16px; margin-top: 4px; }
          .ct-support-icon { font-size: 1.4rem; }
          .ct-support-title { font-size: 1.05rem; margin-bottom: 4px; }
          .ct-support-desc { font-size: 0.85rem; line-height: 1.5; }
        }
      `}</style>
    </>
  );
};

export default ContactUs;
